import { useCallback, useMemo } from "react";
import {
  CEDE_CEXS,
  HubUserCredentials,
  TradeToEarnCampaignStatus,
  TradeToEarnCampaignSummary,
  TradeToEarnCampaignSummarySchema,
  TradingCampaignRules,
  type CampaignsStatsResponse,
} from "@cede/types";
import { API_ENDPOINT, ApiHelper, QueryKeys } from "@cede/utils";
import { TradingCampaignRulesByLabels } from "@/constants/trading";
import { useApi } from "@/hooks/core/useApi";
import { useUserStats } from "@/hooks/data/useUserStats";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useUserSources } from "@/hooks/user/useUserSources";
import { useFiltersStore } from "@/store/filtersStore";
import {
  filterCampaignsByParticipation,
  sortCampaignsByOption,
  sortWithLivePriority,
  sortWithPrivateCampaignsFirst,
  sortWithNewCampaignsFirst,
  transformCampaigns,
} from "@/utils/campaign";
import * as Sentry from "@sentry/nextjs";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

async function fetchCampaignSummaries(
  apiHelper: ApiHelper,
  getSources: () => string[],
  status: TradeToEarnCampaignStatus[],
  size: number,
  auth?: HubUserCredentials,
  rules?: TradingCampaignRules[],
  exchangeIds?: CEDE_CEXS[],
  cursor?: string | null,
  search?: string,
): Promise<{
  campaigns: TradeToEarnCampaignSummary[];
  cursor: string | null;
  totalCount: number;
  stats?: CampaignsStatsResponse;
}> {
  const queryParams: Record<string, string> = {
    utms: getSources().join(","),
    status: status.join(","),
    size: String(size),
    // Arena competitions live in the standalone Arena app.
    isArenaCompetition: "false",
  };

  if (rules && rules.length > 0) {
    queryParams.rules = rules.join(",");
  }

  if (exchangeIds && exchangeIds.length > 0) {
    queryParams.exchangeIds = exchangeIds.join(",");
  }

  if (cursor) {
    queryParams.cursor = cursor;
  }

  if (search) {
    queryParams.search = search;
  }

  let campaigns: {
    campaigns: TradeToEarnCampaignSummary[];
    cursor: string | null;
    totalCount: number;
    stats?: CampaignsStatsResponse;
  } = {
    campaigns: [],
    cursor: null,
    totalCount: 0,
  };
  if (auth) {
    campaigns = await apiHelper.getWithAuth(auth, API_ENDPOINT.TRADE_TO_EARN_CAMPAIGNS_AUTHENTICATED, {
      queryParams,
    });
  } else {
    campaigns = await apiHelper.getOrThrow(API_ENDPOINT.TRADE_TO_EARN_CAMPAIGNS, {
      queryParams,
    });
  }

  const parsedCampaigns = campaigns.campaigns
    .map((campaign) => {
      const parsedCampaign = TradeToEarnCampaignSummarySchema.safeParse(campaign);
      if (!parsedCampaign.success) {
        Sentry.captureException("Invalid TradeToEarnCampaignSummary item", {
          level: "warning",
          extra: { issues: parsedCampaign.error.issues },
        });
        return null;
      }
      return parsedCampaign.data;
    })
    .filter((campaign) => campaign !== null);
  return {
    campaigns: parsedCampaigns,
    cursor: campaigns.cursor,
    totalCount: campaigns.totalCount,
    stats: campaigns.stats,
  };
}

export const useCampaigns = ({
  exchangeIds,
  rules,
  status,
  size = 30,
  enabled = true,
  skipUserStats = false,
}: {
  exchangeIds?: CEDE_CEXS[];
  status: TradeToEarnCampaignStatus[];
  size?: number;
  enabled?: boolean;
  rules?: TradingCampaignRules[];
  /** Skip fetching user stats (participatingCampaigns) - useful for selectors that don't need participation data */
  skipUserStats?: boolean;
}) => {
  const { auth: baseAuth, authenticated, hubUser, isAuthStateStable } = useHubUser();
  const apiHelper = useApi();
  const { getSources } = useUserSources();
  // Only fetch user stats when needed (e.g., for participation filtering on campaigns list page)
  const { participatingCampaigns } = useUserStats({ enabled: !skipUserStats });
  const { sortState, selectedExchanges, selectedStatuses, participationFilter, searchQuery, rewardView } =
    useFiltersStore();

  const sortBy = `${sortState.field}:${sortState.direction}` as const;

  const auth: HubUserCredentials | undefined = useMemo(() => {
    if (authenticated && hubUser?.defiAddress) {
      return baseAuth;
    }
    return undefined;
  }, [authenticated, hubUser?.defiAddress, baseAuth]);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      QueryKeys.CAMPAIGNS_METADATA,
      status.join(","),
      size,
      rules?.join(","),
      exchangeIds?.join(","),
      searchQuery,
      auth?.identityToken,
    ],
    queryFn: async ({ pageParam }) =>
      await fetchCampaignSummaries(
        apiHelper,
        getSources,
        status,
        size,
        auth,
        rules,
        exchangeIds,
        pageParam,
        searchQuery,
      ),
    getNextPageParam: (lastPage) => (lastPage.campaigns.length ? lastPage.cursor : null),
    initialPageParam: undefined as string | undefined,
    enabled: enabled && isAuthStateStable,
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    // Keep previous data while fetching new data (e.g., when auth token changes)
    // This prevents double skeleton flashing during auth state changes
    placeholderData: keepPreviousData,
  });

  // Combine and transform all campaigns from pages
  const campaigns = useMemo(() => {
    if (!data) {
      return null;
    }
    const combinedCampaigns = data.pages.flatMap((page) => page.campaigns);
    const combinedStats = data.pages.reduce((acc, page) => ({ ...acc, ...page.stats }), {} as CampaignsStatsResponse);

    const transformedCampaigns = transformCampaigns(combinedCampaigns, participatingCampaigns, combinedStats);
    const filteredCampaigns = transformedCampaigns.filter((campaign) =>
      filterCampaignsByParticipation(campaign, participationFilter),
    );

    const sortedCampaigns = sortCampaignsByOption(filteredCampaigns, sortBy, rewardView);
    return sortWithNewCampaignsFirst(sortWithPrivateCampaignsFirst(sortWithLivePriority(sortedCampaigns)));
  }, [data, participatingCampaigns, selectedExchanges, selectedStatuses, participationFilter, sortBy, rewardView]);

  const campaignsByPage = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.pages.map((page) => page.campaigns);
  }, [data]);

  const cursor = data?.pages[data.pages.length - 1]?.cursor || null;

  // Include waiting for auth state in the loading state
  const isWaitingForAuth = !isAuthStateStable;

  return {
    pagesCount: data?.pages.length || 0,
    campaigns,
    isLoading: isLoading || isWaitingForAuth,
    cursor,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    campaignsByPage,
    totalCount: data?.pages[data.pages.length - 1]?.totalCount || 0,
  };
};

export const useAllCampaigns = () => {
  const size = 30;
  const { selectedStatuses, selectedRules, selectedExchanges } = useFiltersStore();

  const ruleTypes = selectedRules.length
    ? selectedRules.flatMap((rule) => TradingCampaignRulesByLabels[rule]).filter((rule) => rule !== undefined)
    : undefined;

  const { campaigns, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage, campaignsByPage, totalCount } =
    useCampaigns({
      status: selectedStatuses.length
        ? selectedStatuses
        : [
            TradeToEarnCampaignStatus.LIVE,
            TradeToEarnCampaignStatus.FUTURE,
            TradeToEarnCampaignStatus.PAST,
            TradeToEarnCampaignStatus.PAUSED,
          ],
      size,
      rules: ruleTypes,
      exchangeIds: selectedExchanges,
    });

  // Memoize so consumers can safely use it as a hook/effect dependency without
  // re-triggering on every render of the calling component.
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    campaigns: campaigns || [],
    isLoading,
    hasNextPage,
    loadMore,
    isFetchingNextPage,
    campaignsByPage,
    totalCount,
  };
};
